import { DoneInvokeEvent, StateValue } from "xstate";
import * as H from "history";
import {
  CardPaymentSelectors,
  CartFulfillErrorModal,
  LodgingSelectors,
  ParentState,
  RewardsPaymentSelectors,
  WalletSelectors,
} from "@capone/checkout";
import {
  COMPLETE_BUY_PACKAGE,
  FiatPrice,
  RewardsAccount,
  RewardsPrice,
} from "redmond";
import {
  FulfillSuccess,
  Product,
  PurchaseError,
} from "@b2bportal/purchase-api";
import { PackagesMachineContext } from "./types";
import { Event } from "./events";
import { getUserSelectedRecommendedFlight } from "./selectors/common";

export const stringifyParentStateValue = (state: StateValue) => {
  if (typeof state === "string") return state;
  if (typeof state === "object") {
    const key = Object.keys(state)[0];
    return key;
  }
  return "";
};

export const mapFlightStateToTransitionEvent: Record<string, Event> = {
  [ParentState.contactInformation]: Event.GO_TO_CONTACT_INFORMATION,
  [ParentState.passengerInformation]: Event.GO_TO_PASSENGER_SELECT,
  [ParentState.review]: Event.GO_TO_REVIEW,
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
  [ParentState.seatSelection]: Event.GO_TO_SEAT_SELECTION,
  [ParentState.cardPayment]: Event.GO_TO_CARD_PAYMENT,
};

export const handleFulfillSuccess = (history: H.History) => {
  history.push("/");
};

export const validateContext = (_: PackagesMachineContext) => true;
// Boolean(
//   (ctx.flightShop.shopPricingInfo.fare?.length || -1) > 0 &&
//     ctx.flightSearch.departureDate &&
//     ctx.flightSearch.destination &&
//     ctx.flightSearch.origin &&
//     ctx.flightShop.selectedTrip.outgoingFareId &&
//     ctx.flightShop.selectedTrip.outgoingSliceId &&
//     ctx.flightShop.selectedTrip.tripId &&
//     Object.keys(ctx.flightShop.airports || {}).length > 0
// );

export function integerWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function numberWithCommas(x: number) {
  return (
    integerWithCommas(Math.floor(x)) +
    (x % 1 ? `.${Math.round((x % 1) * 100)}` : "")
  );
}

export const formatCurrency = (price: FiatPrice, discount?: boolean) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currencyCode,
    currencyDisplay: "narrowSymbol",
  }).format(discount ? Math.abs(price.value) * -1 : price.value);

export const formatRewards = (price: RewardsPrice, discount?: boolean) => {
  const priceValueToUse = discount ? Math.abs(price.value) : price.value;

  const isCash = price.currencyDescription?.includes("cash");

  const currencySymbol = isCash ? "$" : "";
  return `${
    discount ? "-" : ""
  }${currencySymbol}${priceValueToUse.toLocaleString("en-US", {
    minimumFractionDigits: isCash ? 2 : 0,
    maximumFractionDigits: isCash ? 2 : 0,
  })} ${price?.currencyDescription}`;
};

export const getRewardsAmountFromFiat = (
  usdAmount: number,
  rewardsAccount: RewardsAccount
): RewardsPrice => {
  const amountToUse =
    usdAmount *
    (rewardsAccount.rewardsBalance.currencyDescription?.includes("cash")
      ? 1
      : 100);

  return {
    ...rewardsAccount.rewardsBalance,
    value: amountToUse,
  };
};

export const trackPollCompleteProperties = {
  eventName: COMPLETE_BUY_PACKAGE,
  // trackProperties returned in pollfulfill response will be also added

  getAdditionalProperties: (
    context: PackagesMachineContext,
    event: DoneInvokeEvent<
      | FulfillSuccess
      | {
          data: Array<PurchaseError>;
          type: CartFulfillErrorModal;
        }
    >
  ) => {
    let reservationId;
    if ("products" in event.data) {
      const packageProduct = event.data.products.find(
        (product) => product.type === Product.Package
      );
      reservationId = packageProduct?.value?.customerReservationId;
    }
    return {
      reservation_id: reservationId,
      total_card_amount_usd: CardPaymentSelectors.getTotalCardPaymentRequired({
        context,
      }),
      card_product_used: CardPaymentSelectors.getSelectedPaymentCardType({
        context,
      }),
      total_rewards_amount_usd:
        RewardsPaymentSelectors.getRewardsFiatAmountToApply({
          context,
        })?.value || 0,

      credit_amt_redeemed: Math.abs(
        WalletSelectors.getTravelWalletCreditToApply({ context })?.amount
          .amount || 0
      ),
      offer_amount_redeemed_usd: Math.abs(
        WalletSelectors.getSelectedOffer({ context })?.amount.amount || 0
      ),
      credit_redeemed: !!WalletSelectors.getTravelWalletCreditToApply({
        context,
      })?.amount.amount,
      offer_used: !!context.wallet.selectedOfferId,
      package_discount_usd: context.redeemedPackageDiscountAmount,
      package_discount_used: !!context.redeemedPackageDiscountAmount,
      recommended_flight_selected: getUserSelectedRecommendedFlight({
        context,
      }),
      lodging_row_index: context.lodgingShop.selectedLodgingIndex,
      account_type_selected: context.selectedAccount?.productDisplayName ?? "",
      entry_point: context.entryPoint,
      ...WalletSelectors.getSelectedOffer({ context })?.trackingPropertiesV2
        ?.properties,
      ...WalletSelectors.getTravelWalletCreditToApply({ context })
        ?.trackingPropertiesV2?.properties,
    };
  },

  getAdditionalEncryptedProperties: (context: PackagesMachineContext) => {
    const encryptedProperties: string[] = [];

    const lodgingTrackingProperties =
      LodgingSelectors.getLodgingFulfillTrackingProperties({
        context: context as any,
      });

    const selectedOffer = WalletSelectors.getSelectedOffer({ context });

    if (lodgingTrackingProperties.encryptedProperties) {
      encryptedProperties.push(lodgingTrackingProperties.encryptedProperties);
    }

    if (selectedOffer?.trackingPropertiesV2?.encryptedProperties) {
      encryptedProperties.push(
        selectedOffer.trackingPropertiesV2.encryptedProperties
      );
    }

    return encryptedProperties;
  },
};
